import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  ENUM_LABELS,
  IMPORT_ENTITY_HINTS,
  type ImportEntity,
  importEntitySchema,
} from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Button } from '#client/components/ui/Button';
import { Card } from '#client/components/ui/Card';
import { useMutation } from '#client/hooks/useMutation';
import { importUrls } from '#client/lib/endpoints';
import { routes } from '#client/lib/routes';
import { formatCount } from '#client/lib/format';
import { uploadWorkbook } from '../import.service';
import { ButtonLink } from '#client/components/ui/ButtonLink';

/**
 * Step one: what is being imported, and the file.
 *
 * The entity is chosen *before* the file, not after, because it decides which
 * columns the mapper offers and which template is worth downloading — and a
 * template offered after the upload is a template nobody uses.
 */
export function UploadStep() {
  const navigate = useNavigate();
  const [entity, setEntity] = useState<ImportEntity>('lab_tests');
  const [file, setFile] = useState<File | null>(null);
  const uploadRequest = useMutation('That file could not be read.');
  const isUploading = uploadRequest.isPending;

  async function handleUpload() {
    if (!file) return;

    const outcome = await uploadRequest.run(() => uploadWorkbook(entity, file));
    if (outcome.ok) void navigate(routes.importRun(outcome.value.run.id));
  }

  return (
    <div className="c-form-stack">
      {uploadRequest.errorMessage && <Banner tone="critical">{uploadRequest.errorMessage}</Banner>}

      <Card title="What are you importing?" subtitle="This decides which columns the sheet needs">
        <div className="c-import-entities" role="radiogroup" aria-label="What are you importing">
          {importEntitySchema.options.map((option) => (
            <label
              key={option}
              className={`c-import-entity${entity === option ? ' c-import-entity--on' : ''}`}
            >
              <input
                type="radio"
                name="entity"
                value={option}
                checked={entity === option}
                onChange={() => setEntity(option)}
              />
              <span className="c-import-entity__name">{ENUM_LABELS.importEntity[option]}</span>
              <span className="c-import-entity__hint">{IMPORT_ENTITY_HINTS[option]}</span>
            </label>
          ))}
        </div>

        <p className="c-field__help">
          Not sure what the columns should be?{' '}
          <a href={importUrls.template(entity)}>Download a blank template</a> — it carries the
          column names and a sheet explaining each one.
        </p>
      </Card>

      <WorkbookCard file={file} onFileChange={setFile} />

      <div className="c-form-actions">
        <ButtonLink variant="secondary" size="md" to={routes.imports}>
          Cancel
        </ButtonLink>
        <Button
          onClick={() => void handleUpload()}
          disabled={!file || isUploading}
          loading={isUploading}
        >
          Read the file
        </Button>
      </div>
    </div>
  );
}

/** The workbook picker, and what was picked. */
function WorkbookCard({
  file,
  onFileChange,
}: {
  file: File | null;
  onFileChange: (file: File | null) => void;
}) {
  return (
    <Card title="The file" subtitle="An Excel workbook, saved as .xlsx">
      <label className="c-field">
        <span className="c-field__label">Workbook</span>
        <input
          className="c-file-input"
          type="file"
          // A hint to the picker, nothing more: the server decides what this
          // is by reading the first four bytes, never by its name or the
          // content type the browser guessed.
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(event) => onFileChange(event.target.files?.[0] ?? null)}
        />
        <span className="c-field__help">
          Up to 15 MB. An older .xls needs saving as .xlsx first — Excel does that from Save As.
        </span>
      </label>

      {file && (
        <dl className="c-summary">
          <div className="c-summary__row">
            <dt>Chosen</dt>
            <dd>{file.name}</dd>
          </div>
          <div className="c-summary__row">
            <dt>Size</dt>
            <dd>{formatCount(file.size / 1024)} kB</dd>
          </div>
        </dl>
      )}
    </Card>
  );
}
